import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import styled, { keyframes, css } from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const LOGO = '/assets/images/logo.png';

// ─── Custom Mascot SVGs ───────────────────────────────────────────────────────
const RABBIT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80">
  <ellipse cx="20" cy="22" rx="8" ry="18" fill="#f5f5f5" stroke="#ddd" stroke-width="1.5"/>
  <ellipse cx="20" cy="22" rx="4.5" ry="13" fill="#ffb3c1"/>
  <ellipse cx="40" cy="22" rx="8" ry="18" fill="#f5f5f5" stroke="#ddd" stroke-width="1.5"/>
  <ellipse cx="40" cy="22" rx="4.5" ry="13" fill="#ffb3c1"/>
  <ellipse cx="30" cy="62" rx="20" ry="16" fill="#f5f5f5" stroke="#ddd" stroke-width="1.5"/>
  <circle cx="30" cy="40" r="20" fill="#f5f5f5" stroke="#ddd" stroke-width="1.5"/>
  <circle cx="22" cy="36" r="4" fill="#2d2d2d"/>
  <circle cx="38" cy="36" r="4" fill="#2d2d2d"/>
  <circle cx="23.5" cy="34.5" r="1.5" fill="white"/>
  <circle cx="39.5" cy="34.5" r="1.5" fill="white"/>
  <ellipse cx="30" cy="43" rx="3" ry="2" fill="#ff8fa3"/>
  <path d="M27 45.5 Q30 48 33 45.5" fill="none" stroke="#d4a0a8" stroke-width="1.2" stroke-linecap="round"/>
  <line x1="33" y1="42" x2="48" y2="40" stroke="#ccc" stroke-width="0.8"/>
  <line x1="33" y1="44" x2="48" y2="44" stroke="#ccc" stroke-width="0.8"/>
  <line x1="27" y1="42" x2="12" y2="40" stroke="#ccc" stroke-width="0.8"/>
  <line x1="27" y1="44" x2="12" y2="44" stroke="#ccc" stroke-width="0.8"/>
  <circle cx="18" cy="42" r="5" fill="#ffb3c1" opacity="0.4"/>
  <circle cx="42" cy="42" r="5" fill="#ffb3c1" opacity="0.4"/>
  <ellipse cx="13" cy="67" rx="7" ry="4.5" fill="#f0f0f0" stroke="#ddd" stroke-width="1.2"/>
  <ellipse cx="47" cy="67" rx="7" ry="4.5" fill="#f0f0f0" stroke="#ddd" stroke-width="1.2"/>
</svg>`;

const CARROT_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 60 80">
  <path d="M30 76 Q10 62 16 26 Q30 20 44 26 Q50 62 30 76Z" fill="#FF6B1A"/>
  <path d="M30 76 Q12 62 16 26 Q30 22 30 26 L30 76Z" fill="#FF8C42"/>
  <path d="M16 32 Q30 28 44 32" fill="none" stroke="#e05510" stroke-width="1.3" stroke-linecap="round"/>
  <path d="M15 41 Q30 37 45 41" fill="none" stroke="#e05510" stroke-width="1.3" stroke-linecap="round"/>
  <path d="M15 50 Q30 46 45 50" fill="none" stroke="#e05510" stroke-width="1.3" stroke-linecap="round"/>
  <path d="M16 59 Q30 55 44 59" fill="none" stroke="#e05510" stroke-width="1.3" stroke-linecap="round"/>
  <path d="M18 68 Q30 64 42 68" fill="none" stroke="#e05510" stroke-width="1.1" stroke-linecap="round"/>
  <path d="M21 27 Q18 42 19 60" stroke="rgba(255,255,255,0.45)" stroke-width="3" stroke-linecap="round" fill="none"/>
  <path d="M30 22 Q20 6 11 11 Q19 16 30 22Z" fill="#22c55e"/>
  <path d="M30 22 Q30 4 30 9 Q30 16 30 22Z" fill="#16a34a"/>
  <path d="M30 22 Q40 6 49 11 Q41 16 30 22Z" fill="#22c55e"/>
  <path d="M30 22 Q38 9 34 15 Q32 19 30 22Z" fill="#4ade80"/>
  <path d="M30 22 Q22 9 26 15 Q28 19 30 22Z" fill="#4ade80"/>
</svg>`;

// ─── 3D Icon SVGs ────────────────────────────────────────────────────────────

const LEAF_3D_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <path d="M32 60 Q8 46 10 24 Q16 6 32 4 Q48 6 54 24 Q56 46 32 60Z" fill="#16a34a"/>
  <path d="M32 60 Q20 46 22 24 Q26 8 32 4 Z" fill="#22c55e"/>
  <path d="M32 60 Q44 46 42 24 Q38 8 32 4 Z" fill="#15803d"/>
  <path d="M32 6 Q44 12 48 28" stroke="#4ade80" stroke-width="3" fill="none" stroke-linecap="round" opacity="0.7"/>
  <path d="M32 56 L32 4" stroke="#166534" stroke-width="1.5" fill="none" opacity="0.3"/>
  <path d="M32 32 Q20 28 16 20" stroke="#166534" stroke-width="1.2" fill="none" opacity="0.3"/>
  <path d="M32 40 Q20 36 16 28" stroke="#166534" stroke-width="1.2" fill="none" opacity="0.3"/>
  <path d="M32 32 Q44 28 48 20" stroke="#166534" stroke-width="1.2" fill="none" opacity="0.3"/>
  <path d="M32 40 Q44 36 48 28" stroke="#166534" stroke-width="1.2" fill="none" opacity="0.3"/>
</svg>`;

const TROPHY_3D_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <ellipse cx="32" cy="61" rx="12" ry="3" fill="rgba(0,0,0,0.12)"/>
  <rect x="23" y="50" width="18" height="5" rx="2.5" fill="#b45309"/>
  <rect x="20" y="54" width="24" height="5" rx="2.5" fill="#92400e"/>
  <rect x="28" y="44" width="8" height="8" fill="#d97706"/>
  <path d="M14 10 Q15 44 32 46 Q49 44 50 10 Z" fill="#fbbf24"/>
  <path d="M22 10 Q23 44 32 46 Q41 44 42 10 Z" fill="#f59e0b"/>
  <rect x="14" y="7" width="36" height="6" rx="3" fill="#fcd34d"/>
  <path d="M14 16 Q6 16 6 26 Q6 36 14 34" stroke="#f59e0b" stroke-width="4" fill="none" stroke-linecap="round"/>
  <path d="M50 16 Q58 16 58 26 Q58 36 50 34" stroke="#f59e0b" stroke-width="4" fill="none" stroke-linecap="round"/>
  <path d="M18 14 Q19 30 22 40" stroke="rgba(255,255,255,0.45)" stroke-width="3" fill="none" stroke-linecap="round"/>
  <text x="32" y="38" text-anchor="middle" dominant-baseline="middle" font-size="16" fill="#92400e">★</text>
</svg>`;

const PUZZLE_3D_SVG = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64">
  <rect x="6" y="6" width="23" height="23" rx="5" fill="#ef4444"/>
  <rect x="35" y="6" width="23" height="23" rx="5" fill="#3b82f6"/>
  <rect x="6" y="35" width="23" height="23" rx="5" fill="#22c55e"/>
  <rect x="35" y="35" width="23" height="23" rx="5" fill="#f59e0b"/>
  <rect x="27" y="14" width="10" height="10" rx="5" fill="#3b82f6"/>
  <rect x="40" y="27" width="10" height="10" rx="5" fill="#f59e0b"/>
  <rect x="14" y="27" width="10" height="10" rx="5" fill="#ef4444"/>
  <rect x="27" y="40" width="10" height="10" rx="5" fill="#22c55e"/>
  <rect x="8" y="8" width="8" height="4" rx="2" fill="rgba(255,255,255,0.4)"/>
  <rect x="37" y="8" width="8" height="4" rx="2" fill="rgba(255,255,255,0.4)"/>
  <rect x="8" y="37" width="8" height="4" rx="2" fill="rgba(255,255,255,0.4)"/>
  <rect x="37" y="37" width="8" height="4" rx="2" fill="rgba(255,255,255,0.4)"/>
</svg>`;

const LEAF_3D_URL   = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(LEAF_3D_SVG)}`;
const TROPHY_3D_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(TROPHY_3D_SVG)}`;
const PUZZLE_3D_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(PUZZLE_3D_SVG)}`;
// Rabbit as transparent SVG for intro (no white bg)
const RABBIT_INTRO_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(RABBIT_SVG)}`;

function _makeSVGImage(svg: string): HTMLImageElement {
  const img = new Image();
  img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  return img;
}
const carrotImg = _makeSVGImage(CARROT_SVG);
const CARROT_DATA_URL = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(CARROT_SVG)}`;

// 3D carrot image
const carrot3dImg = Object.assign(new Image(), { src: '/assets/images/carrot_3d.png' });

// 3D rabbit poses — PNG images
type RabbitPose = 'idle' | 'up' | 'down' | 'left' | 'right';
const rabbitPoseImgs: Record<RabbitPose, HTMLImageElement> = {
  idle:  Object.assign(new Image(), { src: '/assets/images/rabbit_idle.png' }),
  up:    Object.assign(new Image(), { src: '/assets/images/rabbit_up.png' }),
  down:  Object.assign(new Image(), { src: '/assets/images/rabbit_down.png' }),
  left:  Object.assign(new Image(), { src: '/assets/images/rabbit_left.png' }),
  right: Object.assign(new Image(), { src: '/assets/images/rabbit_right.png' }),
};

/** Remove white/near-white background from an image using canvas pixel API. Result is cached. */
const _bgRemovedCache = new Map<HTMLImageElement | HTMLCanvasElement, HTMLCanvasElement>();
function removeBgCanvas(img: HTMLImageElement, threshold = 38, feather = 22): HTMLCanvasElement | null {
  if (!img.complete || img.naturalWidth === 0) return null;
  const cached = _bgRemovedCache.get(img);
  if (cached) return cached;
  try {
    const off = document.createElement('canvas');
    off.width = img.naturalWidth; off.height = img.naturalHeight;
    const ctx2 = off.getContext('2d')!;
    ctx2.drawImage(img, 0, 0);
    const id = ctx2.getImageData(0, 0, off.width, off.height);
    const d = id.data;
    for (let i = 0; i < d.length; i += 4) {
      const wh = Math.min(d[i], d[i+1], d[i+2]);
      if (wh > 255 - threshold) { d[i+3] = 0; }
      else if (wh > 255 - threshold - feather) {
        d[i+3] = Math.round(255 * (1 - (wh - (255 - threshold - feather)) / feather));
      }
    }
    ctx2.putImageData(id, 0, 0);
    _bgRemovedCache.set(img, off);
    return off;
  } catch { return null; }
}



// ─── Maze Data ───────────────────────────────────────────────────────────────

const MAZE_1 = [
  [1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1],
  [1,0,0,0,1,0,0,0,1],
  [1,1,1,1,1,1,1,1,1],
];

const MAZE_2 = [
  [1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,0,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_3 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,0,1,0,1,0,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_4 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,0,0,0,1,0,1],
  [1,0,1,0,0,0,1,1,1,0,1,0,1],
  [1,0,1,1,1,0,0,0,1,0,1,0,1],
  [1,0,0,0,1,1,1,0,1,0,0,0,1],
  [1,1,1,0,0,0,1,0,1,1,1,0,1],
  [1,0,0,0,1,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,1,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,1,1,0,1,0,0,0,1],
  [1,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_5 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,1,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,1,1,0,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,0,1,1,1,0,1,0,0,0,1],
  [1,0,0,0,1,0,0,0,1,0,1,1,1,0,1],
  [1,0,1,1,1,1,1,0,1,0,0,0,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_6 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,1,0,0,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,0,1,0,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,1,1,1,1,0,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,1,0,1,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,0,1,0,1,1,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_7 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,0,1,0,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,1,1,1,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,1,0,1,0,1],
  [1,0,1,1,1,0,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,0,1,1,1,0,1,0,1,1,1,1,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,0,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,1,1,0,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_8 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,0,0,1,0,0,0,1,0,0,0,0,0,1],
  [1,0,1,1,1,0,1,0,1,0,1,0,1,1,1,0,1],
  [1,0,1,0,1,0,0,0,1,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,1,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,1,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,1,1,1,1,0,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,1,0,1,0,0,0,1,0,1],
  [1,1,1,1,1,0,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,1,0,0,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,0,1,1,1,1,1,0,1,0,0,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_9 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,1,1,0,1,0,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,1,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,1,1,0,1,1,1,1,1,1,1,0,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,0,0,0,0,1,0,1,0,1],
  [1,1,1,0,1,0,1,1,1,0,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,1,0,0,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,0,1,0,1,0,1,1,1,1,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,0,1,0,1,1,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,0,0,0,0,1,0,1,0,0,0,1],
  [1,1,1,0,1,1,1,0,1,1,1,0,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,1,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,0,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,1,0,1,0,0,0,0,0,0,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,1,1,1,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

const MAZE_10 = [
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,0,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1,0,1],
  [1,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,1,1,0,1,1,1,1,1,0,1,1,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,1,0,1,0,0,0,1,0,1,0,0,0,1],
  [1,0,1,1,1,1,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1],
  [1,0,1,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,1],
  [1,0,1,0,1,0,1,1,1,1,1,0,1,1,1,1,1,0,1,0,1],
  [1,0,0,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,0,0,1],
  [1,1,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,1,0,1,0,0,0,1,0,1,0,0,0,1,0,1],
  [1,0,1,1,1,0,1,0,1,1,1,0,1,0,1,1,1,0,1,0,1],
  [1,0,1,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,1,0,1],
  [1,0,1,0,1,1,1,1,1,0,1,1,1,1,1,0,1,0,1,0,1],
  [1,0,0,0,0,0,0,0,1,0,0,0,0,0,0,0,0,0,1,0,1],
  [1,1,1,0,1,1,1,0,1,1,1,0,1,1,1,0,1,1,1,0,1],
  [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1],
  [1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1],
];

interface LevelConfig {
  maze: number[][];
  start: [number, number];
  end: [number, number];
  timeLimit: number;
  difficulty: string;
  diffStars: number;
  name: string;
}

const LEVELS: LevelConfig[] = [
  { maze: MAZE_1,  start: [1,1], end: [7,7],   timeLimit: 60,  difficulty: 'Mudah',   diffStars: 1, name: 'The Flower Garden' },
  { maze: MAZE_2,  start: [1,1], end: [9,9],   timeLimit: 90,  difficulty: 'Mudah',   diffStars: 1, name: 'The Carrot Patch' },
  { maze: MAZE_3,  start: [1,1], end: [11,11], timeLimit: 120, difficulty: 'Sedang',  diffStars: 2, name: 'The Garden Maze' },
  { maze: MAZE_4,  start: [1,1], end: [11,11], timeLimit: 90,  difficulty: 'Sedang',  diffStars: 2, name: 'The Forest Path' },
  { maze: MAZE_5,  start: [1,1], end: [13,13], timeLimit: 100, difficulty: 'Susah',   diffStars: 3, name: 'The Deep Woods' },
  { maze: MAZE_6,  start: [1,1], end: [13,13], timeLimit: 90,  difficulty: 'Susah',   diffStars: 3, name: 'The Hedge Maze' },
  { maze: MAZE_7,  start: [1,1], end: [15,15], timeLimit: 100, difficulty: 'Expert',  diffStars: 4, name: 'The Grand Garden' },
  { maze: MAZE_8,  start: [1,1], end: [15,15], timeLimit: 90,  difficulty: 'Expert',  diffStars: 4, name: 'The Ancient Maze' },
  { maze: MAZE_9,  start: [1,1], end: [17,17], timeLimit: 110, difficulty: 'Master',  diffStars: 5, name: "Dragon's Labyrinth" },
  { maze: MAZE_10, start: [1,1], end: [19,19], timeLimit: 120, difficulty: 'Legenda', diffStars: 5, name: 'The Final Frontier' },
];

// ─── Animations ──────────────────────────────────────────────────────────────

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-5deg); }
  50%       { transform: translateY(-12px) rotate(5deg); }
`;

const floatDelay = keyframes`
  0%, 100% { transform: translateY(0px) rotate(8deg); }
  50%       { transform: translateY(-8px) rotate(-3deg); }
`;

const bounce = keyframes`
  0%, 100% { transform: scale(1); }
  40%       { transform: scale(1.15) translateY(-6px); }
  70%       { transform: scale(1.07) translateY(-3px); }
`;

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const fadeInScale = keyframes`
  from { opacity: 0; transform: scale(0.75); }
  to   { opacity: 1; transform: scale(1); }
`;

const confettiFall = keyframes`
  0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
`;

const timerPulse = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.45; }
`;

const rotatePhone = keyframes`
  0%   { transform: rotate(0deg) scale(1); }
  25%  { transform: rotate(-15deg) scale(1.05); }
  55%  { transform: rotate(-90deg) scale(1.1); }
  75%  { transform: rotate(-90deg) scale(1.05); }
  100% { transform: rotate(0deg) scale(1); }
`;

const rabbitJump = keyframes`
  0%, 100% { transform: translateY(0px) scale(1); }
  30%       { transform: translateY(-22px) scale(1.05) rotate(-3deg); }
  50%       { transform: translateY(-28px) scale(1.08) rotate(2deg); }
  70%       { transform: translateY(-14px) scale(1.03) rotate(-1deg); }
  85%       { transform: translateY(-4px) scale(0.97); }
`;

// ─── INTRO SCREEN ─────────────────────────────────────────────────────────────

const IntroOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  background: linear-gradient(160deg, #f9e8d9 0%, #faf0e8 40%, #e8f5f3 100%);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const IntroTopNav = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(45,188,175,0.1);
  padding: 10px 16px;
  display: grid;
  grid-template-columns: 40px 1fr;
  align-items: center;
  gap: 8px;
`;

const NavBackBtn = styled.button`
  width: 36px;
  height: 36px;
  background: transparent;
  color: #333;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  .material-symbols-outlined { font-size: 26px; }
  transition: color 0.2s;
  &:hover { color: #2DBCAF; }
  padding: 0;
`;

const NavLogo = styled.img`
  height: 36px;
`;

const IntroCharArea = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 0;
`;

const FloatingIcon = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $delay?: number }>`
  position: absolute;
  top: ${p => p.$top ?? 'auto'};
  left: ${p => p.$left ?? 'auto'};
  right: ${p => p.$right ?? 'auto'};
  bottom: ${p => p.$bottom ?? 'auto'};
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 14px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06);
  animation: ${p => p.$delay && p.$delay % 2 === 0 ? floatDelay : float} 3.5s ease-in-out ${p => p.$delay ?? 0}s infinite;
  z-index: 20;
  img { width: 28px; height: 28px; object-fit: contain; }
`;

/* Transparent container — just for sizing/centering the rabbit */
const CharacterCard = styled.div`
  width: min(70vw, 280px);
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 2;
`;

const CharacterEmoji = styled.div`
  animation: ${bounce} 2s ease-in-out infinite;
  line-height: 1;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  filter: drop-shadow(0 12px 28px rgba(0,0,0,0.18));
`;

const CarrotBadge = styled.div`
  position: absolute;
  bottom: -8px;
  right: -8px;
  width: min(18vw, 72px);
  height: min(18vw, 72px);
  animation: ${floatDelay} 2.5s ease-in-out infinite;
  z-index: 25;
  img { width: 100%; height: 100%; object-fit: contain; filter: drop-shadow(0 6px 12px rgba(0,0,0,0.25)); }
`;

const IntroInfoCard = styled.div`
  background: #fff;
  border-radius: 32px 32px 0 0;
  padding: 24px 24px max(32px, env(safe-area-inset-bottom));
  box-shadow: 0 -8px 32px rgba(0,0,0,0.08);
  animation: ${fadeInUp} 0.6s ease;
`;

const IntroHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e0e0e0;
  border-radius: 99px;
  margin: 0 auto 18px;
`;

const IntroTitle = styled.h1`
  font-size: clamp(26px, 7vw, 32px);
  font-weight: 900;
  margin: 0 0 8px;
  color: #1a2a2a;
  letter-spacing: -0.5px;
  span { color: #2DBCAF; }
`;

const IntroDesc = styled.p`
  font-size: 14px;
  color: #7a8a8a;
  line-height: 1.6;
  margin: 0 0 20px;
`;

const StartBtn = styled.button`
  width: 100%;
  padding: 16px 24px;
  border-radius: 99px;
  border: none;
  background: linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%);
  color: #fff;
  font-size: 17px;
  font-weight: 800;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(43,188,175,0.4);
  transition: all 0.2s;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 32px rgba(43,188,175,0.5); }
  &:active { transform: translateY(0); }
`;

// ─── GAME SCREEN ─────────────────────────────────────────────────────────────

const GameWrapper = styled.div`
  display: flex;
  flex-direction: row;
  background: #f4f6f8;
  font-family: 'Plus Jakarta Sans', sans-serif;
  overflow: hidden;
  position: fixed;
  inset: 0;

  /* Default: landscape (screen wider than tall) */
  width: 100dvw;
  height: 100dvh;

  /* Auto-rotate when phone is portrait */
  @media screen and (orientation: portrait) {
    width: 100dvh;
    height: 100dvw;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%) rotate(90deg);
    transform-origin: center center;
  }
`;

// ─── LANDSCAPE GAME LAYOUT ───────────────────────────────────────────────────

/* LEFT: maze panel */
const LeftPanel = styled.div`
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-width: 0;
  min-height: 0;
  height: 100%;
`;

/* Canvas fills entire left panel */
const MazeCanvasWrap = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, #e0f2fe, #ecfdf5);
  min-height: 0;
  height: 100%;
`;

/* Floating back button — top left over maze */
const FloatBackBtn = styled.button`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 20;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(8px);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  color: #333;
  .material-symbols-outlined { font-size: 22px; }
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,1); }
`;

/* Mute button — top right over maze */
const MuteBtn = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 20;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255,255,255,0.88);
  backdrop-filter: blur(8px);
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0,0,0,0.15);
  color: #333;
  .material-symbols-outlined { font-size: 20px; }
  transition: background 0.2s;
  &:hover { background: rgba(255,255,255,1); }
`;

/* Info bottom bar over maze */
const MazeInfoBar = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 10;
  background: linear-gradient(to top, rgba(0,0,0,0.35) 0%, transparent 100%);
  padding: 12px 12px 8px;
  pointer-events: none;
`;

const LevelNameFloat = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(255,255,255,0.9);
  letter-spacing: 0.3px;
  margin-bottom: 5px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const TimerBarWrap = styled.div`
  height: 6px;
  background: rgba(255,255,255,0.25);
  border-radius: 99px;
  overflow: hidden;
`;

const TimerBarFill = styled.div<{ $pct: number; $danger: boolean; $warn: boolean }>`
  height: 100%;
  width: ${p => p.$pct}%;
  background: ${p => p.$danger
    ? 'linear-gradient(90deg, #ff4d4f, #ff7875)'
    : p.$warn
    ? 'linear-gradient(90deg, #fa8c16, #ffc069)'
    : 'linear-gradient(90deg, #2DBCAF, #52d9ce)'};
  border-radius: 99px;
  transition: width 0.8s linear;
  ${p => p.$danger && css`animation: ${timerPulse} 0.8s ease-in-out infinite;`}
`;

/* RIGHT: controls panel */
const RightPanel = styled.div`
  width: 152px;
  flex-shrink: 0;
  background: #f8fafc;
  border-left: 1px solid #e2e8f0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 14px 10px 10px;
  gap: 8px;
`;

const TimerBadge = styled.div<{ $danger: boolean; $warn: boolean }>`
  background: ${p => p.$danger ? 'rgba(255,77,79,0.12)' : p.$warn ? 'rgba(250,140,22,0.12)' : 'rgba(45,188,175,0.12)'};
  color: ${p => p.$danger ? '#ff4d4f' : p.$warn ? '#fa8c16' : '#2DBCAF'};
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 1px;
  font-family: 'Courier New', monospace;
  width: 100%;
  text-align: center;
  ${p => p.$danger && css`animation: ${timerPulse} 0.8s ease-in-out infinite;`}
`;

const FullscreenBtn = styled.button`
  width: 34px;
  height: 34px;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  .material-symbols-outlined { font-size: 20px; }
  &:hover { background: #f1f5f9; color: #2DBCAF; }
`;

/* D-Pad section */
const DPadSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
`;

const DPadGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(3, 1fr);
  gap: 6px;
  width: 100%;
  aspect-ratio: 1;
`;

const DPadBtn = styled.button<{ $isEmpty?: boolean }>`
  aspect-ratio: 1;
  border-radius: 14px;
  border: none;
  background: ${p => p.$isEmpty ? 'transparent' : '#2DBCAF'};
  color: #fff;
  cursor: ${p => p.$isEmpty ? 'default' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${p => p.$isEmpty ? 'none' : '0 5px 0 #24968c'};
  transition: transform 0.1s, box-shadow 0.1s;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  pointer-events: ${p => p.$isEmpty ? 'none' : 'auto'};
  overflow: hidden;
  .material-symbols-outlined {
    font-size: 28px;
    font-variation-settings: "'FILL' 0, 'wght' 700, 'GRAD' 0, 'opsz' 24";
  }
  &:active:not(:disabled) {
    transform: translateY(4px);
    box-shadow: none;
  }
  &:disabled { opacity: 0.35; cursor: default; }
`;


// ─── INLINE LEVEL LIST (in RightPanel) ───────────────────────────────────────

const LevelListScroll = styled.div`
  flex: 1;
  width: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 4px 0;
  min-height: 0;
`;

const LevelListItem = styled.button<{ $active: boolean }>`
  width: 100%;
  padding: 7px 8px;
  border-radius: 10px;
  border: 2px solid ${p => p.$active ? '#2DBCAF' : '#e2e8f0'};
  background: ${p => p.$active ? 'linear-gradient(135deg, #e8faf8, #d0f5f1)' : '#fafbfc'};
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
  -webkit-tap-highlight-color: transparent;
  transition: border-color 0.15s, background 0.15s;
  &:hover { border-color: #2DBCAF; background: #f0fdfb; }
`;

const LevelListNum = styled.div`
  font-size: 9px;
  font-weight: 800;
  color: #2DBCAF;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LevelListName = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: #1a2a2a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LevelListStars = styled.div`
  font-size: 9px;
  margin-top: 2px;
`;

// ─── PORTRAIT GATE ────────────────────────────────────────────────────────────

const PortraitGate = styled.div`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    position: fixed;
    top: 0; left: 0; right: 0; bottom: 0;
    width: 100vw;
    height: 100vh;
    min-height: 100dvh;
    z-index: 9999;
    background: linear-gradient(180deg, #1a3a38 0%, #0f2826 50%, #0a1e1c 100%);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0 32px;
    gap: 0;
    overflow: hidden;
  }
`;

/* decorative blobs */
const PortraitGateBlob = styled.div<{ $top?: string; $left?: string; $right?: string; $bottom?: string; $size?: string; $color?: string }>`
  display: none;
  @media screen and (orientation: portrait) {
    display: block;
    position: absolute;
    top: ${p => p.$top ?? 'auto'};
    left: ${p => p.$left ?? 'auto'};
    right: ${p => p.$right ?? 'auto'};
    bottom: ${p => p.$bottom ?? 'auto'};
    width: ${p => p.$size ?? '200px'};
    height: ${p => p.$size ?? '200px'};
    border-radius: 50%;
    background: ${p => p.$color ?? 'rgba(43,188,175,0.12)'};
    filter: blur(40px);
    pointer-events: none;
  }
`;

const PortraitGateLogo = styled.img`display:none;`; /* removed - not needed */

const PortraitGateRabbitWrap = styled.div`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 2;
    margin-bottom: 24px;
  }
`;

const PortraitGateRabbitImg = styled.img`
  width: 170px;
  height: 170px;
  object-fit: contain;
  filter: drop-shadow(0 12px 32px rgba(0,0,0,0.5));
  animation: ${rabbitJump} 1.8s ease-in-out infinite;
`;

const PortraitGateBackBtn = styled.button`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    position: absolute;
    top: max(16px, env(safe-area-inset-top));
    left: 16px;
    width: 40px;
    height: 40px;
    border-radius: 12px;
    background: rgba(255,255,255,0.12);
    border: 1px solid rgba(255,255,255,0.2);
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 10;
    color: rgba(255,255,255,0.85);
    transition: background 0.2s;
    &:active { background: rgba(255,255,255,0.22); }
    .material-symbols-outlined { font-size: 22px; }
  }
`;

const PortraitGatePhoneWrap = styled.div`
  display: flex;
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  align-items: center;
  justify-content: center;
  animation: ${rotatePhone} 3s ease-in-out infinite;
  color: rgba(255,255,255,0.6);
  margin-right: 4px;
`;

const PortraitGateLandscapeHint = styled.div`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: rgba(255,255,255,0.45);
    font-family: 'Plus Jakarta Sans', sans-serif;
    margin-bottom: 24px;
    position: relative;
    z-index: 2;
  }
`;

/* Main game title — most prominent */
const PortraitGateTitle = styled.h2`
  display: none;
  @media screen and (orientation: portrait) {
    display: block;
    font-size: clamp(24px, 7vw, 30px);
    font-weight: 900;
    color: #ffffff;
    margin: 0 0 6px;
    letter-spacing: -0.5px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    line-height: 1.25;
    position: relative;
    z-index: 2;
    span { color: #2DBCAF; }
  }
`;

/* Game subtitle — medium emphasis */
const PortraitGateSub = styled.p`
  display: none;
  @media screen and (orientation: portrait) {
    display: block;
    font-size: 15px;
    color: rgba(255,255,255,0.75);
    line-height: 1.5;
    margin: 0 0 20px;
    font-family: 'Plus Jakarta Sans', sans-serif;
    max-width: 260px;
    position: relative;
    z-index: 2;
    font-weight: 500;
  }
`;

/* Landscape hint pill — least emphasis */
const PortraitGateCaption = styled.div`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    align-items: center;
    gap: 6px;
    background: rgba(255,255,255,0.08);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 99px;
    padding: 6px 14px;
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: rgba(255,255,255,0.5);
    letter-spacing: 0.2px;
  }
`;

const PortraitGateStartBtn = styled.button`
  display: none;
  @media screen and (orientation: portrait) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: min(80vw, 300px);
    padding: 15px 24px;
    border-radius: 99px;
    border: none;
    background: linear-gradient(135deg, #2BBCAF 0%, #1fa99c 50%, #18938a 100%);
    color: #fff;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: 0.3px;
    cursor: pointer;
    box-shadow: 0 8px 28px rgba(43,188,175,0.45);
    font-family: 'Plus Jakarta Sans', sans-serif;
    position: relative;
    z-index: 2;
    transition: all 0.2s;
    &:active { transform: scale(0.97); }
  }
`;

// ─── BOTTOM TABS (in right panel) ────────────────────────────────────────────

const BottomTabs = styled.div`
  display: flex;
  width: 100%;
  border-top: 1px solid #e2e8f0;
  padding-top: 8px;
  gap: 4px;
`;

const TabItem = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.$active ? '#2DBCAF' : '#94a3b8'};
  padding: 4px 0;
  transition: color 0.15s;
  -webkit-tap-highlight-color: transparent;
  .material-symbols-outlined {
    font-size: 22px;
    font-variation-settings: ${p => p.$active
      ? "'FILL' 1, 'wght' 400, 'GRAD' 0, 'opsz' 24"
      : "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24"};
  }
`;

const TabLabel = styled.span`
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

// ─── LEVELS SHEET ─────────────────────────────────────────────────────────────

const LevelsOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(0,0,0,0.55);
  display: flex;
  align-items: flex-end;
  backdrop-filter: blur(4px);
`;

const LevelsSheet = styled.div`
  background: #fff;
  border-radius: 20px 20px 0 0;
  padding: 16px 14px 24px;
  width: 100%;
  max-height: 70vh;
  overflow-y: scroll;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  animation: ${fadeInUp} 0.3s ease;
  touch-action: pan-y;
`;

const SheetHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e0e0e0;
  border-radius: 99px;
  margin: 0 auto 14px;
`;

const SheetTitle = styled.div`
  font-size: 17px;
  font-weight: 800;
  color: #1a2a2a;
  margin-bottom: 14px;
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LevelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
`;

const LevelCard = styled.button<{ $active: boolean; $locked?: boolean }>`
  padding: 12px 10px;
  border-radius: 14px;
  border: 2px solid ${p => p.$active ? '#2DBCAF' : '#eef0f2'};
  background: ${p => p.$active ? 'linear-gradient(135deg, #e8faf8 0%, #d0f5f1 100%)' : p.$locked ? '#f3f4f6' : '#fafbfc'};
  cursor: ${p => p.$locked ? 'not-allowed' : 'pointer'};
  text-align: left;
  transition: all 0.2s;
  position: relative;
  opacity: ${p => p.$locked ? 0.7 : 1};
  &:hover {
    border-color: ${p => p.$locked ? '#eef0f2' : '#2DBCAF'};
    transform: ${p => p.$locked ? 'none' : 'translateY(-1px)'};
    box-shadow: ${p => p.$locked ? 'none' : '0 4px 12px rgba(43,188,175,0.2)'};
  }
`;

const LevelCardNum = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #94a3b8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-bottom: 3px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LevelCardName = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #1a2a2a;
  margin-bottom: 5px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const DiffStars = styled.div`
  font-size: 12px;
`;

// ─── WIN / LOSE MODALS ────────────────────────────────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 28px;
  padding: 28px 24px 24px;
  text-align: center;
  animation: ${fadeInScale} 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
  max-width: 300px;
  width: 88%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;

const ModalEmoji = styled.div`
  font-size: 52px;
  margin-bottom: 10px;
  animation: ${bounce} 1s ease-in-out infinite;
`;

const ModalTitle = styled.div`
  font-size: 24px;
  font-weight: 900;
  color: #1a2a2a;
  margin-bottom: 6px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const ModalDesc = styled.div`
  font-size: 14px;
  color: #7a8a8a;
  margin-bottom: 18px;
  line-height: 1.5;
`;

const StarRow = styled.div`
  font-size: 32px;
  margin: 10px 0 18px;
  letter-spacing: 4px;
`;

const ModalBtnRow = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
`;

/* Lock icon overlay for locked levels */
const LevelLockOverlay = styled.div`
  position: absolute;
  inset: 0;
  border-radius: 14px;
  background: rgba(255,255,255,0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
  .material-symbols-outlined { font-size: 22px; color: #9ca3af; }
`;

/* Login modal specific styles */
const LoginModalImg = styled.img`
  width: 80px;
  height: 80px;
  object-fit: contain;
  margin: 0 auto 8px;
  display: block;
  filter: drop-shadow(0 4px 12px rgba(0,0,0,0.15));
`;

const LoginModalTitle = styled.div`
  font-size: 18px;
  font-weight: 900;
  color: #1a2a2a;
  margin-bottom: 6px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const LoginModalDesc = styled.div`
  font-size: 13px;
  color: #7a8a8a;
  line-height: 1.5;
  margin-bottom: 18px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

const GoogleLoginBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 13px 20px;
  border-radius: 99px;
  border: none;
  background: #4285F4;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  font-family: 'Plus Jakarta Sans', sans-serif;
  box-shadow: 0 4px 16px rgba(66,133,244,0.4);
  transition: all 0.2s;
  &:hover { background: #3b7de8; }
`;

const ModalBtn = styled.button<{ $primary?: boolean }>`
  padding: 11px 20px;
  border-radius: 99px;
  border: ${p => p.$primary ? 'none' : '2px solid #e0e0e0'};
  background: ${p => p.$primary ? 'linear-gradient(135deg, #2BBCAF, #1A9E92)' : '#fff'};
  color: ${p => p.$primary ? '#fff' : '#555'};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: ${p => p.$primary ? '0 4px 16px rgba(43,188,175,0.4)' : 'none'};
  transition: all 0.2s;
  font-family: 'Plus Jakarta Sans', sans-serif;
  &:hover { transform: translateY(-1px); }
  &:active { transform: translateY(0); }
`;

// ─── CONFETTI ─────────────────────────────────────────────────────────────────

const ConfettiPiece = styled.div<{
  $left: number; $delay: number; $duration: number; $color: string; $size: number;
}>`
  position: fixed;
  top: 0;
  left: ${p => p.$left}%;
  width: ${p => p.$size}px;
  height: ${p => p.$size * 0.4}px;
  background: ${p => p.$color};
  border-radius: 3px;
  animation: ${confettiFall} ${p => p.$duration}s ${p => p.$delay}s linear forwards;
  z-index: 1100;
  pointer-events: none;
`;

const CONFETTI_COLORS = ['#2BBCAF','#faad14','#52c41a','#667eea','#f64f59','#eb2f96','#13c2c2'];

function Confetti() {
  const pieces = Array.from({ length: 55 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 1.5,
    duration: 2.5 + Math.random() * 2,
    color: CONFETTI_COLORS[i % CONFETTI_COLORS.length],
    size: 8 + Math.random() * 10,
  }));
  return (
    <>
      {pieces.map(p => (
        <ConfettiPiece key={p.id} $left={p.left} $delay={p.delay}
          $duration={p.duration} $color={p.color} $size={p.size} />
      ))}
    </>
  );
}

// ─── CANVAS 3D MAZE RENDERER ──────────────────────────────────────────────────

const WALL_FRONT   = '#2D6A4F';
const WALL_TOP_C   = '#52B788';
const WALL_TOP2    = '#74C69D';
const WALL_RIGHT   = '#1B4332';
const FLOOR_COLOR  = 'rgba(255,255,255,0.55)';
const FLOOR_GOAL   = '#FFF9C4';
const FLOOR_GOAL2  = '#FFE082';
const GRID_LINE    = 'rgba(43,188,175,0.08)';

function drawMaze3D(
  canvas: HTMLCanvasElement,
  maze: number[][],
  pos: [number, number],
  end: [number, number],
  canvasW: number,
  canvasH: number,
  pose: RabbitPose = 'idle',
  animScale: number = 1,
  glowPhase: number = 0,
) {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  const mazeRows = maze.length;
  const mazeCols = maze[0].length;

  // Fit ENTIRE maze in canvas — portrait orientation
  // Account for 3D depth: top face uses D pixels above, right face uses D to right
  // Compute preliminary D as 26% of cell size
  // CS * (mazeCols + 0.3) <= canvasW  →  CS <= canvasW / (mazeCols + 0.3)
  // CS * (mazeRows + 0.3) <= canvasH  →  CS <= canvasH / (mazeRows + 0.3)
  const csW = Math.floor(canvasW / (mazeCols + 0.4));
  const csH = Math.floor(canvasH / (mazeRows + 0.4));
  const CS  = Math.max(8, Math.min(csW, csH, 44));
  const D   = Math.round(CS * 0.26);

  // Center the maze in canvas (with top margin for 3D depth)
  const totalW = CS * mazeCols + D;
  const totalH = CS * mazeRows + D;
  const offsetX = Math.max(0, Math.floor((canvasW - totalW) / 2));
  const offsetY = Math.max(D, Math.floor((canvasH - totalH) / 2));

  ctx.clearRect(0, 0, canvasW, canvasH);

  // Background gradient
  const bg = ctx.createLinearGradient(0, 0, canvasW, canvasH);
  bg.addColorStop(0, '#e0f2fe');
  bg.addColorStop(1, '#dcfce7');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, canvasW, canvasH);

  const sx = (c: number) => offsetX + c * CS;
  const sy = (r: number) => offsetY + r * CS;

  // PASS 1: Floor cells
  for (let r = 0; r < mazeRows; r++) {
    for (let c = 0; c < mazeCols; c++) {
      if (maze[r][c] !== 0) continue;
      const x = sx(c), y = sy(r);
      const isGoal = r === end[0] && c === end[1];
      if (isGoal) {
        const grd = ctx.createRadialGradient(x + CS/2, y + CS/2, 0, x + CS/2, y + CS/2, CS);
        grd.addColorStop(0, FLOOR_GOAL2);
        grd.addColorStop(1, FLOOR_GOAL);
        ctx.fillStyle = grd;
      } else {
        ctx.fillStyle = FLOOR_COLOR;
      }
      ctx.fillRect(x, y, CS, CS);
      if (CS > 12) {
        ctx.strokeStyle = GRID_LINE;
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x, y, CS, CS);
      }
    }
  }

  // PASS 2: Walls — draw bottom rows first (painter's algorithm)
  for (let r = mazeRows - 1; r >= 0; r--) {
    for (let c = 0; c < mazeCols; c++) {
      if (maze[r][c] !== 1) continue;
      const x = sx(c), y = sy(r);
      const wallAbove = r > 0 && maze[r-1][c] === 1;
      const wallRight = c < mazeCols-1 && maze[r][c+1] === 1;

      // Front face
      ctx.fillStyle = WALL_FRONT;
      ctx.fillRect(x, y, CS, CS);

      // Subtle texture lines (only for larger cells)
      if (CS > 16) {
        ctx.fillStyle = 'rgba(0,0,0,0.06)';
        for (let i = 1; i < 3; i++) {
          ctx.fillRect(x + (CS/3)*i - 1, y + 2, 1, CS - 4);
        }
      }

      // Top shine on front face
      if (CS > 10) {
        const tg = ctx.createLinearGradient(x, y, x, y + CS * 0.4);
        tg.addColorStop(0, 'rgba(82,183,136,0.45)');
        tg.addColorStop(1, 'rgba(82,183,136,0)');
        ctx.fillStyle = tg;
        ctx.fillRect(x, y, CS, CS * 0.4);
      }

      // 3D Top face (exposed only)
      if (!wallAbove) {
        ctx.fillStyle = WALL_TOP_C;
        ctx.beginPath();
        ctx.moveTo(x,        y);
        ctx.lineTo(x + D,    y - D);
        ctx.lineTo(x + CS + D, y - D);
        ctx.lineTo(x + CS,   y);
        ctx.closePath();
        ctx.fill();
        if (D > 2) {
          ctx.fillStyle = WALL_TOP2;
          ctx.fillRect(x + D, y - D, CS, 2);
        }
      }

      // 3D Right face (exposed only)
      if (!wallRight) {
        ctx.fillStyle = WALL_RIGHT;
        ctx.beginPath();
        ctx.moveTo(x + CS,     y);
        ctx.lineTo(x + CS + D, y - D);
        ctx.lineTo(x + CS + D, y + CS - D);
        ctx.lineTo(x + CS,     y + CS);
        ctx.closePath();
        ctx.fill();
      }
    }
  }

  // PASS 3: Goal (carrot) — 3D image + animated yellow glow (no ring)
  {
    const [er, ec] = end;
    const gx = sx(ec) + CS/2;
    const gy = sy(er) + CS/2;
    const size = CS * 1.05;
    // Animated yellow glow — pulses using glowPhase
    const glowIntensity = 0.5 + 0.5 * Math.sin(glowPhase);
    const glowRadius = CS * (0.55 + 0.12 * glowIntensity);
    const glow = ctx.createRadialGradient(gx, gy, 0, gx, gy, glowRadius);
    glow.addColorStop(0, `rgba(255, 230, 0, ${0.55 * glowIntensity})`);
    glow.addColorStop(0.5, `rgba(255, 180, 0, ${0.3 * glowIntensity})`);
    glow.addColorStop(1, 'rgba(255, 150, 0, 0)');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(gx, gy, glowRadius * 1.3, 0, Math.PI * 2);
    ctx.fill();
    // Draw 3D carrot (bg removed)
    const carrotSrc = removeBgCanvas(carrot3dImg) || removeBgCanvas(carrotImg);
    if (carrotSrc) {
      ctx.drawImage(carrotSrc, gx - size/2, gy - size/2, size, size);
    } else if (carrot3dImg.complete && carrot3dImg.naturalWidth > 0) {
      ctx.drawImage(carrot3dImg, gx - size/2, gy - size/2, size, size);
    } else {
      ctx.font = `${Math.max(10, Math.round(CS * 0.6))}px serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('🥕', gx, gy);
    }
  }

  // PASS 4: Player (rabbit) — 3D pose image with bounce animation
  {
    const [pr, pc] = pos;
    const px2 = sx(pc) + CS/2;
    const py2 = sy(pr) + CS/2;
    const baseSize = CS * 1.0;
    const size = baseSize * animScale;
    const img = rabbitPoseImgs[pose];
    const rabbitSrc = removeBgCanvas(img);
    if (rabbitSrc) {
      ctx.drawImage(rabbitSrc, px2 - size/2, py2 - size/2, size, size);
    } else if (img.complete && img.naturalWidth > 0) {
      ctx.drawImage(img, px2 - size/2, py2 - size/2, size, size);
    } else {
      ctx.font = `${Math.max(10, Math.round(CS * 0.72))}px serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('🐰', px2, py2);
    }
  }
}

// ─── Canvas Component ─────────────────────────────────────────────────────────

function MazeCanvasComponent({
  maze, pos, end, lives, maxLives, pose = 'idle', animScale = 1,
}: {
  maze: number[][];
  pos: [number, number];
  end: [number, number];
  lives: number;
  maxLives: number;
  pose?: RabbitPose;
  animScale?: number;
}) {
  const canvasRef  = useRef<HTMLCanvasElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ w: 300, h: 300 });
  const [glowPhase, setGlowPhase] = useState(0);

  // Observe container size
  useEffect(() => {
    if (!wrapperRef.current) return;
    const ro = new ResizeObserver(entries => {
      const entry = entries[0];
      if (!entry) return;
      const w = Math.round(entry.contentRect.width);
      const h = Math.round(entry.contentRect.height);
      if (w > 0 && h > 0) setDims({ w, h });
    });
    ro.observe(wrapperRef.current);
    return () => ro.disconnect();
  }, []);

  // Glow animation — 30fps interval
  useEffect(() => {
    const id = setInterval(() => setGlowPhase(p => p + 0.07), 33);
    return () => clearInterval(id);
  }, []);

  // Draw whenever anything changes
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dims.w <= 0 || dims.h <= 0) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = dims.w * dpr;
    canvas.height = dims.h * dpr;
    canvas.style.width  = dims.w + 'px';
    canvas.style.height = dims.h + 'px';
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.scale(dpr, dpr);
    drawMaze3D(canvas, maze, pos, end, dims.w, dims.h, pose, animScale, glowPhase);
  }, [dims, maze, pos, end, pose, animScale, glowPhase]);

  return (
    <div ref={wrapperRef} style={{ position: 'absolute', inset: 0 }}>
      <canvas ref={canvasRef} style={{ display: 'block' }} />
    </div>
  );
}


let _audioCtx: AudioContext | null = null;
function getAudioCtx(): AudioContext {
  if (!_audioCtx) {
    _audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return _audioCtx;
}

function playMoveSound() {
  try {
    const ctx = getAudioCtx();
    // Funny boing: sine wave, pitch goes up then down quickly
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    const t = ctx.currentTime;
    osc.frequency.setValueAtTime(520, t);
    osc.frequency.exponentialRampToValueAtTime(820, t + 0.04);
    osc.frequency.exponentialRampToValueAtTime(480, t + 0.12);
    gain.gain.setValueAtTime(0.35, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.18);
    osc.start(t);
    osc.stop(t + 0.2);
  } catch (_) {}
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function calcStars(elapsed: number, timeLimit: number): number {
  const pct = elapsed / timeLimit;
  if (pct <= 0.4) return 3;
  if (pct <= 0.7) return 2;
  return 1;
}

function formatTime(secs: number): string {
  const m = Math.floor(secs / 60).toString().padStart(2, '0');
  const s = (secs % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ─── MAIN GAME ────────────────────────────────────────────────────────────────

type GameState = 'playing' | 'win' | 'lose';
type ActiveTab = 'play' | 'levels';

function MazeGame() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showIntro, setShowIntro]         = useState(true);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [pendingNextLevel, setPendingNextLevel] = useState<number | null>(null);
  // maxUnlocked: highest level index the user has reached (0 = only level 1 unlocked)
  const [maxUnlocked, setMaxUnlocked]     = useState(0);
  const [isMuted, setIsMuted]             = useState(false);
  const audioCtxRef  = useRef<AudioContext | null>(null);
  const musicGainRef = useRef<GainNode | null>(null);
  const musicNodesRef= useRef<OscillatorNode[]>([]);

  // ── Background Music (Web Audio synth) ──────────────────────────────────────
  const startMusic = useCallback(() => {
    if (audioCtxRef.current) return;
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
      audioCtxRef.current = ctx;

      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.18, ctx.currentTime);
      masterGain.connect(ctx.destination);
      musicGainRef.current = masterGain;

      // Reverb-like effect using convolver + delay
      const delay = ctx.createDelay(0.5);
      delay.delayTime.value = 0.28;
      const delayGain = ctx.createGain();
      delayGain.gain.value = 0.22;
      delay.connect(delayGain);
      delayGain.connect(delay);
      delayGain.connect(masterGain);

      // Calm pentatonic melody: C4 D4 E4 G4 A4 (loop)
      const notes = [261.63, 293.66, 329.63, 392.00, 440.00, 392.00, 329.63, 293.66];
      const noteDur = 0.55; // seconds per note
      const loopLen = notes.length * noteDur;

      const scheduleLoop = (startAt: number) => {
        notes.forEach((freq, i) => {
          const t = startAt + i * noteDur;
          // Melody oscillator
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.value = freq;
          const g = ctx.createGain();
          g.gain.setValueAtTime(0, t);
          g.gain.linearRampToValueAtTime(0.55, t + 0.04);
          g.gain.exponentialRampToValueAtTime(0.001, t + noteDur * 0.92);
          osc.connect(g);
          g.connect(masterGain);
          g.connect(delay);
          osc.start(t);
          osc.stop(t + noteDur);
          musicNodesRef.current.push(osc);
        });

        // Soft pad chord (C major)
        const padFreqs = [261.63, 329.63, 392.00];
        padFreqs.forEach(freq => {
          const pad = ctx.createOscillator();
          pad.type = 'triangle';
          pad.frequency.value = freq * 0.5; // one octave down
          const pg = ctx.createGain();
          pg.gain.setValueAtTime(0.06, startAt);
          pg.gain.linearRampToValueAtTime(0.09, startAt + loopLen * 0.3);
          pg.gain.linearRampToValueAtTime(0.06, startAt + loopLen);
          pad.connect(pg);
          pg.connect(masterGain);
          pad.start(startAt);
          pad.stop(startAt + loopLen);
          musicNodesRef.current.push(pad);
        });
      };

      // Schedule first two loops ahead
      scheduleLoop(ctx.currentTime + 0.1);
      scheduleLoop(ctx.currentTime + 0.1 + loopLen);

      // Keep looping
      const loopInterval = setInterval(() => {
        if (!audioCtxRef.current) { clearInterval(loopInterval); return; }
        const ctx2 = audioCtxRef.current;
        // Clean up finished nodes
        musicNodesRef.current = musicNodesRef.current.filter(n => {
          try { return n.context.state !== 'closed'; } catch { return false; }
        });
        scheduleLoop(ctx2.currentTime + loopLen);
      }, loopLen * 1000);
    } catch (_) { /* audio not supported */ }
  }, []);

  const stopMusic = useCallback(() => {
    if (audioCtxRef.current) {
      audioCtxRef.current.close();
      audioCtxRef.current = null;
      musicGainRef.current = null;
      musicNodesRef.current = [];
    }
  }, []);

  // Start music when game begins (after intro dismissed)
  useEffect(() => {
    if (!showIntro) startMusic();
    return () => { if (!showIntro) stopMusic(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showIntro]);

  // Mute / unmute
  useEffect(() => {
    if (musicGainRef.current) {
      musicGainRef.current.gain.setTargetAtTime(isMuted ? 0 : 0.18, audioCtxRef.current!.currentTime, 0.1);
    }
  }, [isMuted]);

  // ── Load progress from backend (logged-in users) ─────────────────────────
  useEffect(() => {
    if (!user) return;
    fetch('/api/progress/maze', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data && typeof data.maxLevel === 'number') {
          setMaxUnlocked(data.maxLevel);
          // Resume from last level
          setLevelIdx(data.maxLevel);
          const lv = LEVELS[data.maxLevel];
          if (lv) {
            setPos(lv.start);
            setTimeLeft(lv.timeLimit);
          }
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // ── Save progress to backend ──────────────────────────────────────────────
  const saveProgress = useCallback((levelIdx: number) => {
    if (!user) return;
    fetch('/api/progress/maze', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ maxLevel: levelIdx }),
    }).catch(() => {});
  }, [user]);
  const [facing, setFacing]               = useState<RabbitPose>('idle');
  const [animScale, setAnimScale]         = useState(1);
  const animRafRef                        = useRef<number>(0);
  const [activeTab, setActiveTab]         = useState<ActiveTab>('play');
  const [levelIdx, setLevelIdx]           = useState(0);
  const [pos, setPos]                     = useState<[number, number]>(LEVELS[0].start);
  const [timeLeft, setTimeLeft]           = useState(LEVELS[0].timeLimit);
  const [gameState, setGameState]         = useState<GameState>('playing');
  const [stars, setStars]                 = useState(0);
  const [lives, setLives]                 = useState(3);
  const MAX_LIVES = 3;
  const elapsedRef  = useRef(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const level = LEVELS[levelIdx];

  const resetLevel = useCallback((idx: number) => {
    const lv = LEVELS[idx];
    setPos(lv.start);
    setTimeLeft(lv.timeLimit);
    elapsedRef.current = 0;
    setGameState('playing');
    setStars(0);
    setLives(3);
  }, []);

  // Timer
  useEffect(() => {
    if (showIntro || showLeaveConfirm || gameState !== 'playing') {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(intervalRef.current!);
          setGameState('lose');
          return 0;
        }
        elapsedRef.current += 1;
        return t - 1;
      });
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [gameState, levelIdx, showIntro, showLeaveConfirm]);

  // Movement
  const triggerBounce = useCallback((newPose: RabbitPose) => {
    cancelAnimationFrame(animRafRef.current);
    setFacing(newPose);
    const start = performance.now();
    const duration = 220;
    const step = (now: number) => {
      const t = Math.min((now - start) / duration, 1);
      const scale = t < 0.4
        ? 1 + 0.28 * (t / 0.4)
        : 1 + 0.28 * (1 - (t - 0.4) / 0.6);
      setAnimScale(scale);
      if (t < 1) {
        animRafRef.current = requestAnimationFrame(step);
      } else {
        setAnimScale(1);
        setFacing('idle');
      }
    };
    animRafRef.current = requestAnimationFrame(step);
  }, []);

  const move = useCallback((dr: number, dc: number) => {
    if (gameState !== 'playing') return;
    const pose: RabbitPose = dr < 0 ? 'up' : dr > 0 ? 'down' : dc < 0 ? 'left' : 'right';
    triggerBounce(pose);
    setPos(([r, c]) => {
      const nr = r + dr, nc = c + dc;
      const maze = LEVELS[levelIdx].maze;
      if (nr < 0 || nr >= maze.length || nc < 0 || nc >= maze[0].length) return [r, c];
      if (maze[nr][nc] === 1) return [r, c];
      return [nr, nc];
    });
  }, [gameState, levelIdx, triggerBounce]);

  // Win check
  useEffect(() => {
    if (gameState !== 'playing') return;
    const [er, ec] = level.end;
    if (pos[0] === er && pos[1] === ec) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      setStars(calcStars(elapsedRef.current, level.timeLimit));
      setGameState('win');
      try { new Audio('/assets/sounds/clap-full.mp3').play().catch(() => {}); } catch (_) {}
      // Unlock next level & save progress
      const nextIdx = levelIdx + 1;
      if (nextIdx < LEVELS.length) {
        setMaxUnlocked(prev => Math.max(prev, nextIdx));
        saveProgress(nextIdx);
      }
    }
  }, [pos, gameState, level]);

  // Keyboard
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowUp':    e.preventDefault(); move(-1, 0); break;
        case 'ArrowDown':  e.preventDefault(); move(1, 0);  break;
        case 'ArrowLeft':  e.preventDefault(); move(0, -1); break;
        case 'ArrowRight': e.preventDefault(); move(0, 1);  break;
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [move]);

  const changeLevel = (idx: number) => {
    setLevelIdx(idx);
    resetLevel(idx);
    setActiveTab('play');
  };

  const timerPct = (timeLeft / level.timeLimit) * 100;
  const isDanger = timerPct <= 25;
  const isWarn   = timerPct <= 50 && !isDanger;

  // ── Intro Screen ────────────────────────────────────────────────────────────
  if (showIntro) {
    return (
      <>
      <IntroOverlay>
        <IntroTopNav>
          <NavBackBtn onClick={() => navigate('/')}>
            <span className="material-symbols-outlined">arrow_back</span>
          </NavBackBtn>
          <div />
        </IntroTopNav>

        <IntroCharArea>
          <FloatingIcon $top="16%" $left="10%" $delay={0}>
            <img src={LEAF_3D_URL} alt="leaf" />
          </FloatingIcon>
          <FloatingIcon $top="12%" $right="10%" $delay={1}>
            <img src={TROPHY_3D_URL} alt="trophy" />
          </FloatingIcon>
          <FloatingIcon $bottom="16%" $left="12%" $delay={2}>
            <img src={PUZZLE_3D_URL} alt="puzzle" />
          </FloatingIcon>
          <FloatingIcon $bottom="20%" $right="11%" $delay={3}>
            <img src="/assets/images/carrot_3d.png" alt="carrot" />
          </FloatingIcon>

          <CharacterCard>
            <CharacterEmoji>
              <img
                src="/assets/images/rabbit_jump.png"
                alt="rabbit"
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
              />
            </CharacterEmoji>
            <CarrotBadge>
              <img src="/assets/images/carrot_3d.png" alt="carrot" />
            </CarrotBadge>
          </CharacterCard>
        </IntroCharArea>

        <IntroInfoCard>
          <IntroHandle />
          <IntroTitle>Labirin <span>Taman</span></IntroTitle>
          <IntroDesc>
            Bantu Kelinci menemukan wortel lezat di dalam labirin taman yang seru!
          </IntroDesc>
          <StartBtn onClick={() => {
            setShowIntro(false);
            // Request fullscreen (best-effort, requires user gesture)
            try {
              const el = document.documentElement as any;
              if (el.requestFullscreen) el.requestFullscreen({ navigationUI: 'hide' });
              else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
              else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
            } catch (_) {}
          }}>
            Mulai Bermain
          </StartBtn>
        </IntroInfoCard>

      </IntroOverlay>

      {/* Portrait gate — full screen overlay, only visible in portrait mode */}
      <PortraitGate>
        {/* Decorative blobs */}
        <PortraitGateBlob $top="-60px" $left="-60px" $size="240px" $color="rgba(43,188,175,0.15)" />
        <PortraitGateBlob $bottom="-80px" $right="-80px" $size="280px" $color="rgba(43,188,175,0.10)" />
        <PortraitGateBlob $top="40%" $left="50%" $size="160px" $color="rgba(255,150,50,0.06)" />

        {/* Back button */}
        <PortraitGateBackBtn onClick={() => navigate('/')}>
          <span className="material-symbols-outlined">arrow_back</span>
        </PortraitGateBackBtn>

        {/* Jumping rabbit */}
        <PortraitGateRabbitWrap>
          <PortraitGateRabbitImg src="/assets/images/rabbit_jump.png" alt="rabbit" />
        </PortraitGateRabbitWrap>

        {/* PRIMARY — game context */}
        <PortraitGateTitle>Bantu <span>Kelinci</span> cari wortel!</PortraitGateTitle>
        <PortraitGateSub>Jelajahi labirin taman yang seru dan temukan semua wortelnya sebelum waktu habis!</PortraitGateSub>

        {/* SECONDARY — landscape hint (less prominent) */}
        <PortraitGateLandscapeHint>
          <PortraitGatePhoneWrap>
            <span className="material-symbols-outlined" style={{ fontSize: 30, color: 'rgba(255,255,255,0.7)' }}>screen_rotation</span>
          </PortraitGatePhoneWrap>
          <span>Putar HP ke <strong style={{color:'rgba(255,255,255,0.65)'}}>Landscape</strong> untuk tampilan terbaik</span>
        </PortraitGateLandscapeHint>

        <PortraitGateStartBtn onClick={() => {
          setShowIntro(false);
          try {
            const el = document.documentElement as any;
            if (el.requestFullscreen) el.requestFullscreen({ navigationUI: 'hide' });
            else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
          } catch (_) {}
        }}>
          Mulai Bermain
        </PortraitGateStartBtn>
      </PortraitGate>
      </>
    );
  }

  // ── Game Screen — LANDSCAPE ──────────────────────────────────────────────────
  return (
    <GameWrapper>

      {/* LEFT: Maze panel — fills screen */}
      <LeftPanel>
        <MazeCanvasWrap>
          <MazeCanvasComponent maze={level.maze} pos={pos} end={level.end} lives={lives} maxLives={MAX_LIVES} pose={facing} animScale={animScale} />

          {/* Floating back button */}
          <FloatBackBtn onClick={() => setShowLeaveConfirm(true)}>
            <span className="material-symbols-outlined">arrow_back</span>
          </FloatBackBtn>

          {/* Mute / unmute button */}
          <MuteBtn onClick={() => setIsMuted(m => !m)} title={isMuted ? 'Unmute' : 'Mute'}>
            <span className="material-symbols-outlined">{isMuted ? 'volume_off' : 'volume_up'}</span>
          </MuteBtn>

          {/* Level name + timer bar bottom overlay */}
          <MazeInfoBar>
            <LevelNameFloat>Level {levelIdx + 1}: {level.name}</LevelNameFloat>
            <TimerBarWrap>
              <TimerBarFill $pct={timerPct} $danger={isDanger} $warn={isWarn} />
            </TimerBarWrap>
          </MazeInfoBar>
        </MazeCanvasWrap>
      </LeftPanel>

      {/* RIGHT: Controls panel */}
      <RightPanel>
        {/* Timer */}
        <TimerBadge $danger={isDanger} $warn={isWarn}>
          ⏱ {formatTime(timeLeft)}
        </TimerBadge>

        {/* D-Pad or Level List based on activeTab */}
        {activeTab === 'levels' ? (
          <LevelListScroll>
            {LEVELS.map((lv, i) => {
              const isLocked = i > maxUnlocked;
              return (
              <LevelListItem key={i} $active={i === levelIdx}
                onClick={() => { if (!isLocked) { changeLevel(i); setActiveTab('play'); } }}
                style={{ opacity: isLocked ? 0.5 : 1, cursor: isLocked ? 'not-allowed' : 'pointer' }}>
                <LevelListNum>Lv{i + 1}</LevelListNum>
                <LevelListName>{isLocked ? '🔒 ' : ''}{lv.name}</LevelListName>
                <LevelListStars>{isLocked ? '' : ['⭐','⭐⭐','⭐⭐⭐','💀','💀💀','💀💀💀'][Math.min(lv.diffStars-1,5)]}</LevelListStars>
              </LevelListItem>
            );})}
          </LevelListScroll>
        ) : (
          <DPadSection>
            <DPadGrid>
              <DPadBtn $isEmpty />
              <DPadBtn onClick={() => { playMoveSound(); move(-1, 0); }} disabled={gameState !== 'playing'}>
                <span className="material-symbols-outlined">keyboard_arrow_up</span>
              </DPadBtn>
              <DPadBtn $isEmpty />
              <DPadBtn onClick={() => { playMoveSound(); move(0, -1)} } disabled={gameState !== 'playing'}>
                <span className="material-symbols-outlined">keyboard_arrow_left</span>
              </DPadBtn>
              <DPadBtn $isEmpty />
              <DPadBtn onClick={() => { playMoveSound(); move(0, 1); }} disabled={gameState !== 'playing'}>
                <span className="material-symbols-outlined">keyboard_arrow_right</span>
              </DPadBtn>
              <DPadBtn $isEmpty />
              <DPadBtn onClick={() => { playMoveSound(); move(1, 0); }} disabled={gameState !== 'playing'}>
                <span className="material-symbols-outlined">keyboard_arrow_down</span>
              </DPadBtn>
              <DPadBtn $isEmpty />
            </DPadGrid>
          </DPadSection>
        )}

        {/* Bottom tabs */}
        <BottomTabs>
          <TabItem $active={activeTab === 'play'} onClick={() => setActiveTab('play')}>
            <span className="material-symbols-outlined">sports_esports</span>
            <TabLabel>Play</TabLabel>
          </TabItem>
          <TabItem $active={activeTab === 'levels'} onClick={() => setActiveTab('levels')}>
            <span className="material-symbols-outlined">grid_view</span>
            <TabLabel>Levels</TabLabel>
          </TabItem>
        </BottomTabs>
      </RightPanel>


      {/* Leave Confirmation Popup */}
      {showLeaveConfirm && (
        <ModalOverlay onClick={() => setShowLeaveConfirm(false)}>
          <Modal onClick={e => e.stopPropagation()}>
            <ModalEmoji>🚪</ModalEmoji>
            <ModalTitle>Keluar Permainan?</ModalTitle>
            <ModalDesc>
              Apakah kamu yakin ingin meninggalkan permainan?{'\n'}Progress saat ini tidak akan tersimpan.
            </ModalDesc>
            <ModalBtnRow>
              <ModalBtn onClick={() => setShowLeaveConfirm(false)}>
                Lanjut Main
              </ModalBtn>
              <ModalBtn
                $primary
                style={{ background: 'linear-gradient(135deg, #ff4d4f, #ff7875)', boxShadow: '0 4px 16px rgba(255,77,79,0.4)' }}
                onClick={() => {
                  setShowLeaveConfirm(false);
                  setShowIntro(true);
                  resetLevel(levelIdx);
                  try {
                    const doc = document as any;
                    if (doc.exitFullscreen) doc.exitFullscreen();
                    else if (doc.webkitExitFullscreen) doc.webkitExitFullscreen();
                  } catch (_) {}
                }}
              >
                Keluar
              </ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {/* Win Modal */}
      {gameState === 'win' && (
        <>
          <Confetti />
          <ModalOverlay>
            <Modal>
              <ModalEmoji>🎉</ModalEmoji>
              <ModalTitle>Yeay, Berhasil!</ModalTitle>
              <StarRow>{'⭐'.repeat(stars)}{'☆'.repeat(3 - stars)}</StarRow>
              <ModalDesc>
                {stars === 3 ? 'Luar biasa! Sangat cepat! 🚀'
                  : stars === 2 ? 'Bagus sekali! Terus berlatih! 👍'
                  : 'Keren, kamu bisa lebih cepat! 💪'}
              </ModalDesc>
              <ModalBtnRow>
                <ModalBtn onClick={() => resetLevel(levelIdx)}>Ulangi</ModalBtn>
                {levelIdx < LEVELS.length - 1 && (
                  <ModalBtn $primary onClick={() => {
                    const next = levelIdx + 1;
                    if (!user) {
                      setPendingNextLevel(next);
                      setShowLoginModal(true);
                    } else {
                      changeLevel(next);
                    }
                  }}>
                    Level Berikutnya →
                  </ModalBtn>
                )}
              </ModalBtnRow>
            </Modal>
          </ModalOverlay>
        </>
      )}

      {/* Lose Modal */}
      {gameState === 'lose' && (
        <ModalOverlay>
          <Modal>
            <ModalEmoji>⏰</ModalEmoji>
            <ModalTitle>Waktu Habis!</ModalTitle>
            <ModalDesc>Jangan menyerah! Coba lagi, kamu pasti bisa! 🌟</ModalDesc>
            <ModalBtnRow>
              <ModalBtn $primary onClick={() => resetLevel(levelIdx)}>Coba Lagi</ModalBtn>
            </ModalBtnRow>
          </Modal>
        </ModalOverlay>
      )}

      {/* Login Modal — shown when guest tries to go to next level */}
      {showLoginModal && (
        <ModalOverlay onClick={() => setShowLoginModal(false)}>
          <Modal onClick={e => e.stopPropagation()} style={{ textAlign: 'center' }}>
            <LoginModalImg src="/assets/images/rabbit_jump.png" alt="rabbit" />
            <LoginModalTitle>Login untuk Lanjut! 🔓</LoginModalTitle>
            <LoginModalDesc>
              Simpan progressmu dan lanjutkan ke{' '}
              <strong>Level {(pendingNextLevel ?? 0) + 1}</strong>!{'\n'}
              Login dengan Google — gratis dan cepat.
            </LoginModalDesc>
            <GoogleLoginBtn
              href={`/api/auth/google?returnTo=${encodeURIComponent('/games/maze')}`}
            >
              <svg width="20" height="20" viewBox="0 0 48 48" style={{ flexShrink: 0 }}>
                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8c-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C12.955 4 4 12.955 4 24s8.955 20 20 20s20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                <path fill="#FF3D00" d="m6.306 14.691 6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4C16.318 4 9.656 8.337 6.306 14.691z"/>
                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238A11.91 11.91 0 0 1 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303a12.04 12.04 0 0 1-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
              </svg>
              Masuk dengan Google
            </GoogleLoginBtn>
            <ModalBtn style={{ marginTop: 10, width: '100%' }} onClick={() => setShowLoginModal(false)}>
              Nanti saja
            </ModalBtn>
          </Modal>
        </ModalOverlay>
      )}

    </GameWrapper>
  );
}

export default MazeGame;
